import React, { memo, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import RightModalLayout from '../layouts/Modal/RightModalLayout';
import ManageCustomerAddressMC from '../modules/inbox/components/ManageCustomerAddressMC';
import { useTranslation } from 'react-i18next';
import { SIDEBAR_ACTIONS } from '../../constants/actionsConstants';
import useCustomerAddressActions from '../hooks/inbox/actions/useCustomerAddressActions';
import useCustomerAddressForm from '../hooks/inbox/forms/useCustomerAddressForm';
import FormProviderMC from '../components/FormProviderMC';
import { getCountryOptions } from '../../services/countryService';
import { useTraceUpdate } from '../hooks/shared/useDebugRenders';

const CustomerAddressSidebar = memo(({
  customer,
  updateList,
  selectedAddress,
  loadLocation = false,
  fakeAddress = false,
  hideNotification = false,
  ...restProps
}) => {
  const { t } = useTranslation('InboxMC');
  const dispatch = useDispatch();

  useTraceUpdate('CustomerAddressSidebar', {
    customer,
    updateList,
    selectedAddress,
    loadLocation,
    fakeAddress,
    hideNotification,
    ...restProps
  });

  const handleClickOnClose = useCallback(() => {
    dispatch({
      type: SIDEBAR_ACTIONS.CLOSE_SIDEBAR,
      payload: { componentKey: 'CustomerAddressSidebar' }
    });
  }, [dispatch]);
  // Memoizar countryOptions para evitar recreación en cada render
  const countryOptions = useMemo(() => getCountryOptions(), []);

  // Hook de forms layer para validación y estado del formulario
  const {
    formMethods,
    setErrorMain,
    onPlaceChanged,
    openSelectAddressModal,
    allRequiredFilled,
    ...restPropsForm
  } = useCustomerAddressForm({
    selectedAddress,
    loadLocation,
    countryOptions
  });

  // Hook de capa de acciones (Actions Layer)
  const { loading, handleSaveButtonClick } = useCustomerAddressActions({
    formMethods,
    updateList,
    customer,
    selectedAddress,
    fakeAddress,
    hideNotification,
    setErrorMain,
    handleClickOnClose,
  });
  return (
    <RightModalLayout
      title={t('location.modal.title')}
      cancelLabel={t('customerDetails.cancel')}
      submitLabel={t('customerDetails.save')}
      cancelAction={handleClickOnClose}
      submitAction={handleSaveButtonClick}
      cancelDisabled={loading}
      submitDisabled={loading || !allRequiredFilled}
      closeAction={handleClickOnClose}
    >
      <FormProviderMC methods={formMethods}>
        <ManageCustomerAddressMC
          formMethods={formMethods}
          selectedAddress={selectedAddress}
          countryOptions={countryOptions}
          onPlaceChanged={onPlaceChanged}
          openSelectAddressModal={openSelectAddressModal}
          {...restProps}
          {...restPropsForm}
        />
      </FormProviderMC>
    </RightModalLayout>
  );
});

CustomerAddressSidebar.displayName = 'CustomerAddressSidebar';

export default CustomerAddressSidebar;